import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";

const LANGUAGES = [
  { code: "ja", label: "JP" },
  { code: "en", label: "EN" },
  { code: "zh", label: "中文" },
  { code: "ko", label: "한국어" },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  };

  const goHome = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? "bg-white/95 backdrop-blur-sm border-b border-gray-100" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a
          href="/"
          className="flex items-center gap-2 cursor-pointer"
          onClick={goHome}
        >
          <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
            <img
              src="https://public.readdy.ai/ai/img_res/936a3815-94a7-4322-ba1f-21b9078bb21c.png"
              alt="SHOP-MK logo"
              className={`w-full h-full object-contain transition-all duration-300 ${scrolled ? "" : "brightness-0 invert"}`}
            />
          </div>
          <span className={`font-black text-lg md:text-xl tracking-widest transition-colors duration-300 ${scrolled ? "text-[#1a2332]" : "text-white"}`}>
            SHOP-MK
          </span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          <Link
            to="/about"
            className={`text-sm font-medium tracking-wider transition-colors duration-300 cursor-pointer whitespace-nowrap hover:opacity-70 ${
              scrolled ? "text-[#1a2332]" : "text-white"
            }`}
          >
            {t("nav.about")}
          </Link>
          {[
            { key: "nav.services", id: "services" },
            { key: "nav.partners", id: "partners" },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className={`text-sm font-medium tracking-wider transition-colors duration-300 cursor-pointer whitespace-nowrap hover:opacity-70 ${
                scrolled ? "text-[#1a2332]" : "text-white"
              }`}
            >
              {t(item.key)}
            </button>
          ))}
          <Link
            to="/process"
            className={`text-sm font-medium tracking-wider transition-colors duration-300 cursor-pointer whitespace-nowrap hover:opacity-70 ${
              scrolled ? "text-[#1a2332]" : "text-white"
            }`}
          >
            {t("process.navLink")}
          </Link>
          <button
            onClick={() => scrollTo("contact")}
            className={`text-sm font-medium tracking-wider transition-colors duration-300 cursor-pointer whitespace-nowrap hover:opacity-70 ${
              scrolled ? "text-[#1a2332]" : "text-white"
            }`}
          >
            {t("nav.contact")}
          </button>
        </nav>

        {/* Right: Lang + CTA */}
        <div className="hidden md:flex items-center gap-4">
          {/* Language Switcher */}
          <div className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className={`flex items-center gap-1 text-xs font-medium tracking-wider px-3 py-1.5 rounded-full border transition-all duration-300 cursor-pointer whitespace-nowrap ${
                scrolled
                  ? "border-gray-300 text-gray-600 hover:border-[#1a2332]"
                  : "border-white/40 text-white hover:border-white"
              }`}
            >
              <span>{currentLang.label}</span>
              <i className="ri-arrow-down-s-line text-xs"></i>
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden min-w-[120px]">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap ${
                      i18n.language === lang.code ? "text-[#1a2332] font-semibold" : "text-gray-600"
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* CTA Button */}
          <button
            onClick={() => scrollTo("contact")}
            className="bg-[#1a2332] text-white text-sm font-medium px-5 py-2.5 rounded-full hover:bg-[#2d3f55] transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            {t("nav.contactBtn")}
          </button>
        </div>

        {/* Mobile: Lang + Hamburger */}
        <div className="flex md:hidden items-center gap-3">
          <div className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border transition-all cursor-pointer ${
                scrolled ? "border-gray-300 text-gray-600" : "border-white/40 text-white"
              }`}
            >
              {currentLang.label}
              <i className="ri-arrow-down-s-line text-xs"></i>
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden min-w-[110px] z-50">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors cursor-pointer ${
                      i18n.language === lang.code ? "text-[#1a2332] font-semibold" : "text-gray-600"
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className={`w-8 h-8 flex items-center justify-center cursor-pointer ${scrolled ? "text-[#1a2332]" : "text-white"}`}
          >
            <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-4">
          <Link
            to="/about"
            onClick={() => setMenuOpen(false)}
            className="text-left text-sm font-medium text-[#1a2332] tracking-wider py-2 border-b border-gray-50 cursor-pointer"
          >
            {t("nav.about")}
          </Link>
          {[
            { key: "nav.services", id: "services" },
            { key: "nav.partners", id: "partners" },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="text-left text-sm font-medium text-[#1a2332] tracking-wider py-2 border-b border-gray-50 cursor-pointer"
            >
              {t(item.key)}
            </button>
          ))}
          <Link
            to="/process"
            onClick={() => setMenuOpen(false)}
            className="text-left text-sm font-medium text-[#1a2332] tracking-wider py-2 border-b border-gray-50 cursor-pointer"
          >
            {t("process.navLink")}
          </Link>
          <button
            onClick={() => scrollTo("contact")}
            className="text-left text-sm font-medium text-[#1a2332] tracking-wider py-2 border-b border-gray-50 cursor-pointer"
          >
            {t("nav.contact")}
          </button>
          <button
            onClick={() => scrollTo("contact")}
            className="bg-[#1a2332] text-white text-sm font-medium px-5 py-3 rounded-full mt-2 cursor-pointer whitespace-nowrap"
          >
            {t("nav.contactBtn")}
          </button>
        </div>
      )}
    </header>
  );
}
